import { WorkspaceMinimalFragment } from '@sketch/gql-types'

import { useIsProfileCreated } from '../../../../community/hooks'
import { useFlag } from '@sketch/modules-common'

import {
  useRefetchWorkspaceSettingsOnRouteChange,
  useWorkspaceSettings,
} from '../../../operations'

import {
  useCanSubscribeViaStripe,
  workspaceSettingsPermissions,
} from '../../../utils'

import { CanRenderOptions } from '../../../types'

export const useMenuPermissions = ({
  workspace,
}: {
  workspace: WorkspaceMinimalFragment
}) => {
  const { isProfileCreated } = useIsProfileCreated(workspace.identifier)
  const isPartialSharingEnabled = useFlag('partial-sharing')
  const isByokEnabled = useFlag('byok-access')
  const isIntegrationsEnabled = useFlag('workspace-integrations')

  const { data: workspaceSettingsData, refetch } =
    useWorkspaceSettings(workspace)

  useRefetchWorkspaceSettingsOnRouteChange(refetch, workspace.identifier)

  const { workspace: workspaceSettings, customer } = workspaceSettingsData || {}

  const { source, customerProvider, loading } = useCanSubscribeViaStripe(
    workspace.identifier,
    workspace.customer?.identifier
  )

  const {
    isPartner,
    isFinance,
    isGuest,
    showWorkspaceSettingsBilling,
    showWorkspaceSettingsGeneral,
    showWorkspaceSettingsSSO,
    showWorkspaceSettingsEncryption,
    showWorkspaceSettingsPeople,
    showWorkspacePermissionGroup,
    showWorkspaceSettingsProfile,
    showDocumentPreviews,
    showWorkspaceSettingsIntegrations,
  } = workspaceSettingsPermissions({
    workspace,
    customerProvider,
    settings: workspaceSettings,
    customer,
    source,
    isByokEnabled,
    isProfileCreated,
    isPartialSharingEnabled,
    isIntegrationsEnabled,
  })

  const canRender: CanRenderOptions = {
    generalMenu: showWorkspaceSettingsGeneral,
    billingMenu: showWorkspaceSettingsBilling,
    permissionGroups: showWorkspacePermissionGroup,
    SSOMenu: showWorkspaceSettingsSSO,
    peopleMenu: showWorkspaceSettingsPeople,
    profileMenu: showWorkspaceSettingsProfile,
    documentPreviews: showDocumentPreviews,
    encryptionMenu: showWorkspaceSettingsEncryption,
    integrationsMenu: showWorkspaceSettingsIntegrations,
  }

  return { isFinance, isGuest, isPartner, loading, canRender }
}
